import { CircleInfoIcon, FormGroup, Label, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { useField, useFormikContext } from "formik";
import { isString } from "lodash";
import React, { createContext, ReactNode } from "react";

export const FormFieldContext = createContext<string>("");

interface FormFieldProps {
    name: string;
    label?: string | ReactNode;
    preventLabelClick?: boolean;
    shortLabelError?: boolean;
    className?: string;
    prevalidate?: boolean;
    children: ReactNode;
    tooltip?: ReactNode;
    placement?: string;
    classNames?: {
        label?: string;
    };
}

// TODO: Consider moving to `ui-kit` at some point.
export const FormField = ({
    name,
    label = " ",
    preventLabelClick = false,
    shortLabelError,
    className,
    prevalidate,
    children,
    tooltip,
    placement = "auto",
    classNames,
}: FormFieldProps) => {
    const hasLabel = (isString(label) && label.trim().length > 0) || (label && !isString(label));
    const displayLabel = hasLabel ? label : " ";
    const re = new RegExp(`^${name} `, "i"); // Remove the field name from the error message (if it's at the start)
    const error = useError(name, prevalidate)?.replace(re, "");
    const testId = `label-${name}`;

    const handleLabelClick = (event: MouseEvent) => {
        if (preventLabelClick) {
            event.preventDefault();
        }
    };

    return (
        <FormGroup className={className}>
            {hasLabel && (
                <Label
                    data-testid={testId}
                    data-error={error}
                    className={clsx("whitespace-nowrap", error && "!text-danger", classNames?.label)}
                    onClick={handleLabelClick}
                >
                    {displayLabel} {shortLabelError ? "" : error}{" "}
                    {tooltip && (
                        <Tooltip placement={placement} content={tooltip}>
                            <CircleInfoIcon />
                        </Tooltip>
                    )}
                </Label>
            )}

            {/* TODO: If hasLabel it should be inside label so they get attached to inputs */}
            <FormFieldContext.Provider value={name}>{children}</FormFieldContext.Provider>
        </FormGroup>
    );
};

export const useError = (name: string, prevalidate?: boolean) => {
    const [, meta] = useField(name);
    const formik = useFormikContext();

    // Show errors only after the form is submitted for the first time.
    if (formik.submitCount > 0 || prevalidate) {
        return meta.error ?? null;
    }

    return null;
};
